import { z } from 'zod';

/**
 * One choice in a picker: the value a form submits, the label a person reads, and optional
 * secondary text that tells two similar labels apart.
 *
 * @since 1.4.0
 */
export const optionSchema = z.object({
    value: z.string(),
    label: z.string(),
    hint: z.string().nullable().default(null),
});

export type Option = z.infer<typeof optionSchema>;

/**
 * What every picker endpoint returns. `hasMore` says the cap was reached, so a caller can
 * tell a short list from a truncated one and ask for a narrower search instead of quietly
 * offering a choice that is missing.
 *
 * @since 1.4.0
 */
export const optionsResponseSchema = z.object({
    options: z.array(optionSchema),
    hasMore: z.boolean(),
});

export type OptionsResponse = z.infer<typeof optionsResponseSchema>;

/**
 * The most choices one picker response may be asked for. A caller wanting more than this is
 * not really driving a picker any more, so the request is refused rather than served.
 *
 * @since 1.4.0
 */
export const OPTIONS_MAX_LIMIT = 1_000;

/**
 * How many choices a picker endpoint returns when the query names no limit, and so where a
 * picker stops offering one long list and starts offering a search instead.
 *
 * This is a usability threshold, not a storage one: a plain select stops being something a
 * person can find a name in long before it stops being something the browser can render. Set
 * it near where scrolling gets tiresome, and the search takes over from there.
 *
 * @since 1.4.0
 */
export const OPTIONS_DEFAULT_LIMIT = 100;

/**
 * What every picker endpoint accepts: a search term to narrow the list by, and a cap.
 *
 * @since 1.4.0
 */
export const optionsQuerySchema = z.object({
    q: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(OPTIONS_MAX_LIMIT).default(OPTIONS_DEFAULT_LIMIT),
});

export type OptionsQuery = z.infer<typeof optionsQuerySchema>;
